"use client";

import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { User, ArrowLeftIcon, CreditCard, LogOut, Menu, X, Cable, Settings, ExternalLink } from "lucide-react";

const McpIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="currentColor" fillRule="evenodd" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M15.688 2.343a2.588 2.588 0 00-3.61 0l-9.626 9.44a.863.863 0 01-1.203 0 .823.823 0 010-1.18l9.626-9.44a4.313 4.313 0 016.016 0 4.116 4.116 0 011.204 3.54 4.3 4.3 0 013.609 1.18l.05.05a4.115 4.115 0 010 5.9l-8.706 8.537a.274.274 0 000 .393l1.788 1.754a.823.823 0 010 1.18.863.863 0 01-1.203 0l-1.788-1.753a1.92 1.92 0 010-2.754l8.706-8.538a2.47 2.47 0 000-3.54l-.05-.049a2.588 2.588 0 00-3.607-.003l-7.172 7.034-.002.002-.098.097a.863.863 0 01-1.204 0 .823.823 0 010-1.18l7.273-7.133a2.47 2.47 0 00-.003-3.537z" />
    <path d="M14.485 4.703a.823.823 0 000-1.18.863.863 0 00-1.204 0l-7.119 6.982a4.115 4.115 0 000 5.9 4.314 4.314 0 006.016 0l7.12-6.982a.823.823 0 000-1.18.863.863 0 00-1.204 0l-7.119 6.982a2.588 2.588 0 01-3.61 0 2.47 2.47 0 010-3.54l7.12-6.982z" />
  </svg>
);
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Footer } from "@/components/Footer";
import { ProfileContent } from "@/components/profile/ProfileContent";
import { SubscriptionContent } from "@/components/profile/SubscriptionContent";
import { SettingsContent } from "@/components/profile/SettingsContent";
import { NotionIntegrationCard } from "@/components/NotionIntegrationCard";
import { McpContent } from "@/components/profile/McpContent";
import { fetchUserProfile, type UserProfile } from "@/services/api";
import toast from "react-hot-toast";
import { motion } from "framer-motion";
import { trackProfileViewed, trackSubscriptionViewed, trackSettingsViewed } from "@/utils/eventTracking";

type ActionItem = "home" | "profile" | "subscription" | "integrations" | "settings" | "mcp";

interface UserInfo extends Omit<UserProfile, 'premium_until'> {
  premiumUntil: string | null;
}

// MCP Tile shown in Integrations tab
const McpTile = () => {
  const navigate = useNavigate();

  return (
    <div className="w-full max-w-md bg-gray-800 border border-gray-700 rounded-xl p-5 flex flex-col">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 bg-gray-700 rounded-lg flex items-center justify-center">
          <McpIcon className="w-4 h-4 text-purple-400" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-white font-semibold text-sm">MCP Integration</h3>
            <Badge className="bg-orange-500/20 text-orange-300 border border-orange-500/30 text-xs py-0">Beta</Badge>
          </div>
          <p className="text-gray-400 text-xs">Model Context Protocol</p>
        </div>
      </div>
      <p className="text-gray-400 text-sm mt-4">
        Connect your vault to Claude, ChatGPT, Claude Code, and any other MCP-compatible AI tool.
      </p>
      <div className="flex-1" />
      <Button
        variant="outline"
        size="sm"
        onClick={() => navigate("/profile?active=mcp")}
        className="w-full mt-4 bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600 hover:text-white"
      >
        <ExternalLink className="h-4 w-4 mr-2" />
        View Details
      </Button>
    </div>
  );
};

// Integrations Content Component
const IntegrationsContent = ({ userInfo }: { userInfo: UserInfo; premiumUntil: string | null; reload: boolean }) => {

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">Integrations</h2>
        <p className="text-gray-400">
          Connect your favorite tools to automatically sync your social media posts.
        </p>
      </div>

      <div className="flex flex-wrap gap-4">
        <NotionIntegrationCard premiumUntil={userInfo.premiumUntil} />
        <McpTile />
      </div>
    </div>
  );
};

const actionItems = [
  {
    id: "home",
    label: "Back to Home",
    icon: ArrowLeftIcon,
    component: null,
    hidden: false,
  }, {
    id: "profile",
    label: "Profile",
    icon: User,
    component: ProfileContent,
    hidden: false,
  },
  {
    id: "subscription",
    label: "Manage Your Subscription",
    icon: CreditCard,
    component: SubscriptionContent,
    hidden: false,
  },
  {
    id: "integrations",
    label: "Integrations",
    icon: Cable,
    component: IntegrationsContent,
    hidden: false,
  },
  {
    id: "settings",
    label: "Communication Preferences",
    icon: Settings,
    component: SettingsContent,
    hidden: false,
  },
  {
    id: "mcp",
    label: "MCP Integration",
    icon: McpIcon,
    component: McpContent,
    hidden: true,
  },
] as const;

const isValidActionItem = (item: string | null): item is ActionItem => {
  if (!item) return false;
  return actionItems.some(action => action.id === item);
};

export function ProfilePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const isInitializing = useRef(true);

  // Reset initialization flag when menu opens
  useEffect(() => {
    if (isMobileNavOpen) {
      isInitializing.current = true;
      // Set a timeout to mark initialization as complete
      setTimeout(() => {
        isInitializing.current = false;
      }, 100);
    }
  }, [isMobileNavOpen]);

  // Get active item from URL or default to "profile" (support ?active=settings for post-login redirect)
  const activeTab = searchParams.get("active");
  const reload = searchParams.get("reload");
  const activeItem = isValidActionItem(activeTab) ? activeTab : "profile";

  // Update URL when active item changes
  const handleActionItemClick = (itemId: ActionItem) => {
    if (itemId === "home") {
      navigate("/");
      return;
    }
    
    // Track subscription viewed when subscription tab is clicked
    if (itemId === "subscription") {
      trackSubscriptionViewed();
    }
    if (itemId === "settings") {
      trackSettingsViewed();
    }

    setSearchParams({ active: itemId });
    // Only close menu if it's a user click, not initialization
    if (isMobileNavOpen && !isInitializing.current) {
      setIsMobileNavOpen(false);
    }
  };

  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        setIsLoading(true);
        const profile = await fetchUserProfile();
        setUserInfo({
          ...profile,
          premiumUntil: profile.premium_until
        });
        localStorage.setItem('premiumUntil', profile.premium_until || '')
        
        // Track profile view
        trackProfileViewed();
      } catch (error) {
        console.error('Failed to fetch user profile:', error);
        toast.error('Failed to load profile information');
      } finally {
        setIsLoading(false);
      }
    };

    loadUserProfile();
  }, []);

  // Track settings view when landing on settings tab (e.g. post-login redirect)
  useEffect(() => {
    if (activeItem === "settings") {
      trackSettingsViewed();
    }
  }, [activeItem]);

  const ActiveComponent = actionItems.find(item => item.id === activeItem)?.component;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1 }}
          className="min-h-screen flex flex-col bg-gray-900"
        >
          {/* Top Header Bar */}
          <div className="bg-gray-800 border-b border-gray-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <h1 className="text-2xl font-bold text-white">Instavault</h1>
                <span data-slot="badge" className="inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&amp;>svg]:size-3 gap-1 [&amp;>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden [a&amp;]:hover:bg-secondary/90 bg-purple-500/20 text-purple-300 border-purple-500/30">Settings</span>
              </div>
            </div>
          </div>
          <div className="flex-1 container mx-auto px-4 py-8 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500" />
          </div>
        </motion.div>
        <Footer />
      </div>
    );
  }

  if (!userInfo) {
    return (
      <div className="min-h-screen bg-gray-900">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1 }}
          className="min-h-screen flex flex-col bg-gray-900"
        >
          {/* Top Header Bar */}
          <div className="bg-gray-800 border-b border-gray-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <h1 className="text-2xl font-bold text-white">Instavault</h1>
                <span data-slot="badge" className="inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&amp;>svg]:size-3 gap-1 [&amp;>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden [a&amp;]:hover:bg-secondary/90 bg-purple-500/20 text-purple-300 border-purple-500/30">Settings</span>
              </div>
            </div>
          </div>
          <div className="flex-1 container mx-auto px-4 py-8 flex items-center justify-center">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-white mb-2">Failed to load profile</h2>
              <p className="text-gray-400 mb-4">Please try refreshing the page</p>
              <Button
                variant="outline"
                onClick={() => window.location.reload()}
                className="bg-gray-800 border-gray-600 text-white hover:bg-gray-700"
              >
                Refresh Page
              </Button>

              <Button
                variant="outline"
                onClick={() => {
                  logout();
                  navigate("/");
                }}
                className="bg-gray-800 border-gray-600 text-red-400 hover:text-red-300 hover:bg-gray-700 ml-4"
              >
                Log Out
              </Button>
            </div>
          </div>
        </motion.div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 1 }}
        className="min-h-screen flex flex-col bg-gray-900"
      >
        {/* Top Header Bar */}
        <div className="bg-gray-800 border-b border-gray-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h1 className="text-2xl font-bold text-white">Instavault</h1>
              <span data-slot="badge" className="inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&amp;>svg]:size-3 gap-1 [&amp;>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden [a&amp;]:hover:bg-secondary/90 bg-purple-500/20 text-purple-300 border-purple-500/30">Settings</span>
            </div>
          </div>
        </div>

        <div className="flex-1 flex flex-col md:flex-row relative">
          {/* Mobile Header */}
          <div className="md:hidden flex justify-between items-center mb-4 p-4 ">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMobileNavOpen(!isMobileNavOpen)}
              className="text-white !bg-transparent border-gray-400 focus:!bg-transparent active:!bg-transparent hover:!bg-transparent"
            >
              <Menu className="h-5 w-5" />
            </Button>
          </div>

          {/* Sidebar - Hidden on mobile unless menu is open */}
          <div className={`${isMobileNavOpen ? 'block' : 'hidden'} md:block absolute md:relative top-0 left-0 w-64 md:w-auto h-full md:h-auto z-50 md:z-auto bg-gray-900 md:bg-transparent`}>
            {/* Close button for mobile - positioned within sidebar */}
            {isMobileNavOpen && (
              <div className="md:hidden absolute top-4 left-4 z-10">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsMobileNavOpen(false)}
                  className="text-white hover:bg-gray-700 bg-transparent border-gray-400"
                >
                  <X className="h-6 w-6" />
                </Button>
              </div>
            )}
            <div className="w-64 bg-slate-900/30 p-6 h-full border-r border-gray-700">
              {/* Settings Header */}
              <div className="mb-8">
                <h3 className={`text-sm font-semibold text-gray-400 mb-4 tracking-wider uppercase ${isMobileNavOpen ? "ml-10" : "ml-0"}`}>Settings</h3>
                <div className="space-y-2">
                  {actionItems.filter((item) => !item.hidden).map((item) => (
                    <div
                      key={item.id}
                      className={`w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center space-x-3 ${activeItem === item.id
                        ? "bg-purple-500/20 text-white"
                        : "text-gray-300 hover:bg-gray-700 hover:text-white"
                        }`}
                      onClick={() => handleActionItemClick(item.id)}
                    >
                      <item.icon className="w-4 h-4" />
                      <span className="text-sm font-medium">{item.label}</span>
                    </div>
                  ))}
                  <div
                    className="w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center space-x-3 text-red-400 hover:text-red-300 hover:bg-gray-700"
                    onClick={() => {
                      logout();
                      navigate("/");
                    }}
                  >
                    <LogOut className="w-4 h-4" />
                    <span className="text-sm font-medium">Logout</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div
            className="flex-1 bg-slate-900/30 p-6 overflow-auto"
            onClick={() => {
              if (isMobileNavOpen) {
                setIsMobileNavOpen(false);
              }
            }}
          >
            {ActiveComponent && (
              <ActiveComponent
                userInfo={userInfo}
                premiumUntil={userInfo.premiumUntil}
                reload={reload === "true"}
              />
            )}
          </div>
        </div>
      </motion.div>
      <Footer />
    </div>
  );
} 